/**
 * Skill result renderer — Streamdown + mermaid diagrams + Monaco-editor code
 * blocks + table-rendered csv/json data blocks.
 *
 * Mirrors `ncoder-ai/apps/shell-web/src/pages/dev-agent/chat/DevAgentChatMessageContent.tsx`,
 * minus the chat-store-specific Apex actions (Add to Workspace / Compare) and
 * artifact markers — those depend on `useDevAgentStore` which doesn't exist
 * here. What's preserved:
 *   - `streamdown` for streaming-safe markdown.
 *   - `@streamdown/mermaid` for diagram rendering (with `mermaidScrub`).
 *   - `@streamdown/code` as the default-language fallback (shiki highlighting).
 *   - Custom Monaco renderer for code blocks — same languages the chat uses.
 *   - Custom data renderer for csv/tsv/json/yaml — copy + download.
 */
import { createContext, memo, useContext, useMemo } from "react";
import { MermaidScrollGuard } from "@/components/molecules/MermaidScrollGuard";
import { Streamdown, defaultRemarkPlugins } from "streamdown";
import { mermaid } from "@streamdown/mermaid";
import { code } from "@streamdown/code";
import "streamdown/styles.css";
import "./streamdownOverrides.css";
import { cn } from "@/lib/studio/utils";
import { fixMermaidUnquotedLabels } from "@/lib/studio/mermaidScrub";
import { buildSkillCodeRenderer } from "./SkillCodeRenderer";
import { buildSkillDataRenderer } from "./SkillDataRenderer";
import { CollapsibleMarkdownList } from "./CollapsibleMarkdownList";
import { ReleaseScanReport, parseReleaseScanSse } from "./ReleaseScanReport";
import { maybeMetadataLink } from "@/lib/studio/runs";
import { safeWebUrl } from "@/lib/safeUrl";

const BASE_PLUGINS = { mermaid, code };

/**
 * How an `artifact://` link renders in THIS subtree: `(artifactId, linkText) => node`.
 *
 * The default renderer below has no artifact to open, so with no provider the link is its label as
 * plain text (unchanged). A surface that CAN open one provides the control here instead of passing
 * its own `a` override through `components`: an override replaces the whole link renderer, and the
 * safe-scheme rule for every OTHER link would then have to be written a second time.
 */
export const ArtifactLinkContext = createContext<((artifactId: string, children: React.ReactNode) => React.ReactNode) | null>(null);

function ArtifactLinkSlot({ artifactId, children, ...rest }: { artifactId: string; children?: React.ReactNode } & Record<string, unknown>) {
  const render = useContext(ArtifactLinkContext);
  if (render && artifactId) return <>{render(artifactId, children)}</>;
  return <span {...rest}>{children}</span>;
}

// remark plugin: rewrite genuine `artifact://` link nodes to a hash form so
// Streamdown's URL hardening — which hard-blocks custom schemes (verified that
// NO Streamdown prop, incl. urlTransform/allowedLinkPrefixes/allowedProtocols,
// disables it) — doesn't replace them with a "[blocked]" placeholder. Hash URLs
// are always allowed by the hardener; the link components below strip the
// leading "#" to recover the artifact id and render the CitationChip/editor.
// AST-scoped (unlike a string replace) so it never rewrites text inside code
// or inline-code. Appended to defaultRemarkPlugins so gfm (tables!) is kept.
function remarkArtifactScheme() {
  return (tree: unknown) => {
    const walk = (node: unknown): void => {
      const n = node as { type?: string; url?: string; children?: unknown[] };
      if (n && n.type === "link" && typeof n.url === "string" && n.url.startsWith("artifact://")) {
        n.url = `#${n.url}`;
      }
      (n?.children || []).forEach(walk);
    };
    walk(tree);
  };
}
const REMARK_PLUGINS = [...Object.values(defaultRemarkPlugins), remarkArtifactScheme];

// Allow only safe link targets. Bare/relative/anchor/query links are in-app and
// safe; absolute links must be http/https/mailto. Everything else (javascript:,
// data:, vbscript:, file:, etc.) is treated as unsafe and rendered as inert text.
// Parsed, not pattern-matched: `java\nscript:` passed the old regex and still ran.
function isSafeHref(href: string): boolean {
  return safeWebUrl(href, { allowMailto: true }) !== null;
}

export interface SkillResultMarkdownProps {
  content: string;
  className?: string;
  isStreaming?: boolean;
  components?: Record<string, unknown>;
  /**
   * Replace the two fenced-block renderers, keeping everything else in the pipeline.
   *
   * WHY THIS EXISTS RATHER THAN A `components.pre` OVERRIDE. A `components` entry for `pre` does
   * not layer on top of the block renderers — it REPLACES the path they live on, and takes the
   * mermaid plugin and shiki with it. Weave V1 did exactly that and lost diagram rendering, every
   * copy action and all syntax highlighting in one line of code (measured; see `ChatCodeBlock`).
   *
   * `renderers` is the extension point the block renderers themselves register through, so a
   * surface that wants a different BODY for a fence can swap them here and keep the rest: mermaid
   * still draws, tables still render, links are still hardened, long lists still collapse.
   *
   * Omitted, the defaults apply — a Monaco per code fence and a Monaco per data fence, which is
   * what the report and dev-agent surfaces want.
   */
  codeRenderers?: readonly unknown[];
}

function SkillResultMarkdownInner({
  content,
  className,
  isStreaming = false,
  components,
  codeRenderers,
}: SkillResultMarkdownProps) {
  const text = useMemo(() => {
    // The `artifact://` -> `#artifact://` rewrite is now done AST-side by the
    // remarkArtifactScheme plugin (see REMARK_PLUGINS), not a string replace.
    return fixMermaidUnquotedLabels(decodeEntities(content));
  }, [content]);
  const releaseScanReport = useMemo(() => parseReleaseScanSse(text), [text]);

  const plugins = useMemo(
    () => ({
      ...BASE_PLUGINS,
      renderers:
        codeRenderers ?? [
          buildSkillCodeRenderer({ isStreaming }),
          buildSkillDataRenderer({ isStreaming }),
        ],
    }),
    [codeRenderers, isStreaming]
  );
  const mergedComponents = useMemo(
    () => ({
      // Long enumerations collapse to their first 30 items behind a toggle — see
      // CollapsibleMarkdownList for why this is a head slice and why it stands down
      // while streaming. Declared BEFORE the caller's `components` spread so a
      // surface that needs its own list renderer can still take it back.
      ul: ({ children: kids, className: cls }: { children?: React.ReactNode; className?: string }) => (
        <CollapsibleMarkdownList className={cls} isStreaming={isStreaming}>
          {kids}
        </CollapsibleMarkdownList>
      ),
      ol: ({ children: kids, className: cls, start }: { children?: React.ReactNode; className?: string; start?: number }) => (
        <CollapsibleMarkdownList ordered start={start} className={cls} isStreaming={isStreaming}>
          {kids}
        </CollapsibleMarkdownList>
      ),
      /*
       * `node` IS DESTRUCTURED OFF AND DROPPED, and that is not cosmetic. react-markdown passes the
       * mdast node to every component override; spreading `...rest` onto the `<a>` therefore put
       * `node="[object Object]"` — an invalid attribute — on EVERY link the app renders, and React
       * logs an unknown-prop warning for each one. Verified in the rendered markup.
       */
      a: ({ href, children, node: _node, ...rest }: { href?: string; children?: React.ReactNode } & Record<string, unknown>) => {
        // Undo the `#artifact://` hash-rewrite (see `text` above). Artifact
        // citations have no navigable target in this default renderer (the
        // CitationChip interceptor is only wired in ResultColumn), so render
        // the label as plain text rather than a dead/blocked link.
        const deHashed = href && href.startsWith("#artifact://") ? href.slice(1) : href;
        if (deHashed && deHashed.startsWith("artifact://")) {
          const artifactId = deHashed.slice("artifact://".length).split("?")[0].trim();
          return <ArtifactLinkSlot artifactId={artifactId} {...rest}>{children}</ArtifactLinkSlot>;
        }
        const next = deHashed ? maybeMetadataLink(deHashed) : deHashed;
        // Defense-in-depth: only render navigable links for safe schemes
        // (http/https/mailto) or in-app relative/anchor targets. Dangerous
        // schemes (javascript:, data:, vbscript:) are rendered as inert text so
        // a malicious LLM/artifact link can never execute — even if the
        // markdown hardener config changes upstream.
        if (next && !isSafeHref(next)) {
          return <span {...rest}>{children}</span>;
        }
        const external = Boolean(next && /^(?:https?:)?\/\//.test(next));
        return (
          <a
            {...rest}
            href={next}
            target={external ? "_blank" : (rest.target as string | undefined)}
            rel={external ? "noreferrer" : (rest.rel as string | undefined)}
          >
            {children}
          </a>
        );
      },
      ...(components || {}),
    }),
    [components, isStreaming]
  );

  if (releaseScanReport) {
    return (
      <div className={cn("chat-streamdown skill-streamdown", className?.trim() ? className : "text-foreground")}>
        <ReleaseScanReport report={releaseScanReport} />
      </div>
    );
  }

  return (
    <div className={cn("chat-streamdown skill-streamdown", className?.trim() ? className : "text-foreground")}>
      <MermaidScrollGuard>
        <Streamdown
          // Closing half-typed `**`/`__` is only for a stream still arriving. On settled text it
          // invents markup: a Salesforce name (`Employee_Project__c`) leaves an odd `__`, and remend
          // appended a closing `__` to the user's own question.
          parseIncompleteMarkdown={isStreaming}
          remarkPlugins={REMARK_PLUGINS as never}
          plugins={plugins as never}
          components={mergedComponents as never}
          controls={{ mermaid: false }}
          isAnimating={isStreaming}
          animated
        >
          {text}
        </Streamdown>
      </MermaidScrollGuard>
    </div>
  );
}

export const SkillResultMarkdown = memo(SkillResultMarkdownInner);
SkillResultMarkdown.displayName = "SkillResultMarkdown";

function decodeEntities(s: string): string {
  if (!s) return "";
  if (!/&(?:lt|gt|amp|quot|#39|#x27);/.test(s)) return s;
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&amp;/g, "&");
}
